'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ctfImage } from '@/lib/contentful-url';
import { Markdown } from '@/lib/markdown';
import { trackClarityEvent } from '@/lib/microsoft-clarity';
import { solutionHref } from '@/lib/routes';
import { IRelatedSolution } from '@/models/warehousingSolution';

/** Solutions index — the destination of the section's "View all" link. */
const SOLUTIONS_INDEX_HREF = '/warehousing-solutions';

interface IRelatedSolutions {
	className?: string;
	title?: string;
	solutions: IRelatedSolution[];
	viewAllHref?: string;
}

export default function RelatedSolutions(props: IRelatedSolutions) {
	if (!props.solutions?.length) {
		return null;
	}

	return (
		<section className={`container ${props.className ?? ''}`}>
			<div className="mb-8 md:mb-12 flex items-baseline justify-between gap-6">
				<h2 className="text-2xl md:text-3xl font-bold text-primary-600">
					{props.title ?? 'Explore related Solutions'}
				</h2>

				<Link
					prefetch={false}
					href={props.viewAllHref ?? SOLUTIONS_INDEX_HREF}
					onClick={() => trackClarityEvent('related_solutions_view_all_click')}
					className="flex-shrink-0 text-sm font-semibold text-primary-500 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
				>
					View all
				</Link>
			</div>

			<div className="grid gap-6 md:gap-8 grid-cols-1 md:grid-cols-3 items-stretch">
				{props.solutions.map((solution) => (
					<Link
						key={solution.slug}
						prefetch={false}
						href={solutionHref(solution)}
						onClick={() =>
							trackClarityEvent('related_solution_card_click', {
								related_solution: solution.slug ?? '',
							})
						}
						className="group flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
					>
						{solution.cardImage?.url ? (
							<div className="aspect-video w-full flex-shrink-0 overflow-hidden">
								<Image
									src={ctfImage(solution.cardImage.url, {
										width: 800,
										quality: 75,
									})}
									alt={solution.cardImage.title ?? solution.title}
									width={800}
									height={450}
									sizes="(min-width: 768px) 33vw, 100vw"
									className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
								/>
							</div>
						) : null}

						<h3 className="px-2 mt-4 md:text-2xl font-bold text-primary-600 line-clamp-2 text-[#0A0A0A]">
							{solution.title}
						</h3>

						{!!solution.excerpt ? (
							<div className="px-2  flex-1 text-gray-600">
								<Markdown
									content={solution.excerpt}
									className="[&_p]:line-clamp-3 [&_p]:mb-0 [&_p]:text-sm [&_p]:md:text-base"
								/>
							</div>
						) : null}

						<span className="px-2 mt-4 text-sm font-semibold text-primary-500 group-hover:underline">
							Read more
						</span>
					</Link>
				))}
			</div>
		</section>
	);
}
